import { useEffect, useRef, useState } from "react";
import { io } from "socket.io-client";
import { FaPaperPlane } from "react-icons/fa";

/**
 * AIMentorPanel — chat with the AI startup mentor about a validated idea.
 * Wires to POST /idea-validation/mentor/chat and streams the reply over
 * Socket.io (mentor:stream chunks → mentor:complete).
 *
 * Props:
 *   reportId       {string|number}
 *   userId         {string}
 *   initialHistory {Array}  — pre-loaded [{role, content}] from DB (optional)
 */
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function AIMentorPanel({ reportId, userId, initialHistory = [] }) {
  const [messages, setMessages] = useState(initialHistory);  // pre-seed with DB history
  const [streaming, setStreaming] = useState("");
  const [input, setInput]         = useState("");
  const [busy, setBusy]           = useState(false);
  const socketRef = useRef(null);
  const scrollRef = useRef(null);

  // Connect socket once, listen for mentor stream events
  useEffect(() => {
    if (!userId) return;
    const socket = io(BACKEND, { withCredentials: true, transports: ["websocket", "polling"] });
    socketRef.current = socket;

    socket.on("connect", () => socket.emit("join_user_room", userId));
    socket.on("mentor:stream", ({ chunk }) => setStreaming((s) => s + chunk));
    socket.on("mentor:complete", ({ reply }) => {
      setMessages((m) => [...m, { role: "assistant", content: reply }]);
      setStreaming("");
      setBusy(false);
    });

    return () => socket.disconnect();
  }, [userId]);

  // Auto-scroll to bottom on new content
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, streaming]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    const history = messages.slice(-10);
    setMessages((m) => [...m, { role: "user", content: text }]);
    setInput("");
    setBusy(true);
    setStreaming("");
    try {
      await fetch(`${BACKEND}/idea-validation/mentor/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ reportId, message: text, history }),
      });
      // reply arrives via socket (mentor:stream / mentor:complete)
    } catch (err) {
      console.error("Mentor chat error:", err);
      setMessages((m) => [...m, { role: "assistant", content: "Sorry, I couldn't reach the mentor. Please try again." }]);
      setBusy(false);
    }
  };

  const suggestions = [
    "How can I reduce my CAC?",
    "What's my biggest risk?",
    "Suggest a better revenue model",
  ];

  return (
    <div className="mentor-panel">
      <div className="mentor-head">
        <span className="mentor-badge">✦ AI MENTOR</span>
        <span className="mentor-sub">Ask anything about your idea</span>
      </div>

      <div className="mentor-messages" ref={scrollRef}>
        {messages.length === 0 && !streaming && (
          <div className="mentor-empty">
            <p>Your AI startup mentor has read your full validation. Ask away 👇</p>
            <div className="mentor-suggestions">
              {suggestions.map((s) => (
                <button key={s} className="mentor-chip" onClick={() => setInput(s)}>{s}</button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m, i) => (
          <div key={i} className={`mentor-msg ${m.role}`}>
            <div className="mentor-bubble">{m.content}</div>
          </div>
        ))}

        {streaming && (
          <div className="mentor-msg assistant">
            <div className="mentor-bubble">{streaming}<span className="mentor-caret">▍</span></div>
          </div>
        )}
        {busy && !streaming && (
          <div className="mentor-msg assistant">
            <div className="mentor-bubble mentor-typing"><span></span><span></span><span></span></div>
          </div>
        )}
      </div>

      <div className="mentor-input-row">
        <input
          className="mentor-input"
          placeholder="Message your mentor…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          disabled={busy}
        />
        <button className="mentor-send" onClick={send} disabled={busy || !input.trim()}>
          <FaPaperPlane />
        </button>
      </div>
    </div>
  );
}
